import {useMemo, useState} from 'react';
import {format} from 'date-fns';
import {Link} from 'react-router-dom';
import {useGetMigrationRunsQuery} from '~/store/rtkQuery';
import {Migrations} from '~/modules/api';
import {CreateMigrationRunModal} from './CreateMigrationRunModal';

export const MigrationRuns = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const {data, isLoading, error, refetch} = useGetMigrationRunsQuery();

  const sortedData = useMemo(() => {
    return data
      ? [...data].sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        )
      : undefined;
  }, [data]);

  const getStatusBadge = (status: Migrations.MigrationRunStatus) => {
    const statusColors = {
      [Migrations.MigrationRunStatus.created]:
        'badge badge-info text-info-content',
      [Migrations.MigrationRunStatus.scanning]:
        'badge badge-warning text-warning-content',
      [Migrations.MigrationRunStatus.queued]:
        'badge badge-warning text-warning-content',
      [Migrations.MigrationRunStatus.processing]:
        'badge badge-warning text-warning-content',
      [Migrations.MigrationRunStatus.completed]:
        'badge badge-success text-success-content',
      [Migrations.MigrationRunStatus.failed]:
        'badge badge-error text-error-content',
    };

    return <span className={statusColors[status]}>{status}</span>;
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div role="alert" className="alert alert-error">
          <span>Error: {error.message}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Breadcrumb Navigation */}
      <div className="text-sm breadcrumbs mb-4">
        <ul>
          <li>
            <Link to="/">Home</Link>
          </li>
          <li>Migrations</li>
        </ul>
      </div>

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Migration Runs</h1>
        <button
          className="btn btn-primary"
          onClick={() => setShowCreateModal(true)}>
          Create New Migration
        </button>
      </div>

      {!sortedData?.length ? (
        <div className="text-center py-8">
          <p className="text-gray-500">No migration runs found</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="table table-zebra w-full">
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>Created</th>
                <th>Completed</th>
                <th>Progress</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map(run => (
                <tr key={run.id}>
                  <td>
                    <div className="font-medium">{run.name}</div>
                    <div className="text-sm text-gray-500">
                      Max batches: {run.maxConcurrentBatches}
                    </div>
                  </td>
                  <td>{getStatusBadge(run.status)}</td>
                  <td>
                    <div className="text-sm">
                      {format(new Date(run.createdAt), 'MMM dd, yyyy')}
                    </div>
                    <div className="text-xs text-gray-500">
                      {format(new Date(run.createdAt), 'HH:mm')}
                    </div>
                  </td>
                  <td>
                    {run.completedAt ? (
                      <>
                        <div className="text-sm">
                          {format(new Date(run.completedAt), 'MMM dd, yyyy')}
                        </div>
                        <div className="text-xs text-gray-500">
                          {format(new Date(run.completedAt), 'HH:mm')}
                        </div>
                      </>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td>
                    {run.rowStatusCounts ? (
                      <div className="text-sm">
                        <div>Total: {run.rowStatusCounts.created}</div>
                        <div className="text-success font-medium">
                          Success: {run.rowStatusCounts.success}
                        </div>
                        <div className="text-error font-medium">
                          Error: {run.rowStatusCounts.error}
                        </div>
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td>
                    <Link
                      to={`/system/migrations/${run.id}`}
                      className="btn btn-sm btn-outline">
                      View Details
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CreateMigrationRunModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={() => {
          setShowCreateModal(false);
          refetch();
        }}
      />
    </div>
  );
};
